import type { CommissionPlan } from './fees'
import { BROKER_NAMES } from './brokers'
import { z } from 'zod'

/**
 * Trade workers — the contract between the AI (which only ever fills in a WorkerSpec), the
 * system (which compiles it into exact broker orders + a computed summary) and the user
 * (who approves exactly that compiled plan).
 *
 * Execution model:
 *  - Every share is protected AT THE BROKER: the position is split into "lots", each lot is
 *    one IBKR bracket (entry parent + take-profit + stop-loss children, auto-OCA'd by IBKR),
 *    so a stop keeps working even if our server is down.
 *  - Entry activation conditions (price/%/volume of any stock or index, time, executions,
 *    margin) are IBKR-native order conditions, evaluated by IBKR.
 *  - Everything else (stop ratchets, time exits, cross-instrument and cross-worker triggers)
 *    is an event → action rule executed by our engine on IBKR's push events.
 *
 * All times are US/Eastern wall-clock strings "YYYY-MM-DD HH:MM".
 */

export const WORKER_SPEC_VERSION = 1

const Price = z.number().finite().positive().max(1_000_000)
const Cmp = z.enum(['>=', '<='])
const TierNo = z.number().int().min(1).max(6)

export const SymbolSchema = z
  .string()
  .trim()
  .transform((s) => s.toUpperCase().replace(/\.US$/, ''))
  .refine((s) => /^[A-Z][A-Z0-9.\-]{0,11}$/.test(s), 'invalid US symbol')

export const EtDateTimeSchema = z
  .string()
  .trim()
  .refine((s) => {
    const m = /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2})$/.exec(s)
    if (!m) return false
    const [, y, mo, d, h, mi] = m.map(Number)
    const dt = new Date(Date.UTC(y, mo - 1, d, h, mi))
    return dt.getUTCFullYear() === y && dt.getUTCMonth() === mo - 1 && dt.getUTCDate() === d && h < 24 && mi < 60
  }, 'expected "YYYY-MM-DD HH:MM" (US/Eastern)')

/** A tradable or referenceable instrument. Indices (SPX, NDX, VIX, RUT…) use secType IND. */
export const InstrumentSchema = z.object({
  symbol: SymbolSchema,
  secType: z.enum(['STK', 'IND']).default('STK'),
  /** Listing exchange — needed for most indices (e.g. SPX → CBOE). Resolved + shown on the card. */
  exchange: z
    .string()
    .trim()
    .transform((s) => s.toUpperCase())
    .refine((s) => /^[A-Z0-9.]{2,12}$/.test(s), 'invalid exchange')
    .optional(),
})
export type Instrument = z.infer<typeof InstrumentSchema>

/** How many shares a ladder tier takes. `rest` = everything the ladder has not assigned yet. */
export const TierQtySchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('shares'), shares: z.number().positive().max(1_000_000) }),
  z.object({
    kind: z.literal('portion'),
    num: z.number().int().positive().max(1000),
    den: z.number().int().positive().max(1000),
    /** position = of the full planned quantity; remaining = of what earlier tiers left. */
    of: z.enum(['position', 'remaining']).default('position'),
  }),
  z.object({ kind: z.literal('rest') }),
])
export type TierQty = z.infer<typeof TierQtySchema>

export const TakeProfitTierSchema = z.object({
  /** Limit price of the take-profit order. */
  price: Price,
  qty: TierQtySchema,
})
export type TakeProfitTier = z.infer<typeof TakeProfitTierSchema>

export const StopLossTierSchema = z.object({
  /** Stop (trigger) price. With `trail`, the initial stop. */
  price: Price,
  qty: TierQtySchema,
  /** Stop-limit instead of stop-market: the worst price the stop may fill at. */
  limitPrice: Price.optional(),
  /** Broker-native trailing stop: trails by a fixed amount or a percent. */
  trail: z
    .object({ amount: Price.optional(), percent: z.number().positive().max(50).optional() })
    .refine((t) => (t.amount == null) !== (t.percent == null), 'give exactly one of amount | percent')
    .optional(),
  /** Broker-native one-step ratchet: once the price touches `trigger`, this stop becomes `stop`. */
  adjust: z.object({ trigger: Price, stop: Price }).optional(),
  /**
   * What makes IBKR trigger the stop: its default (the last price for most US stocks), the last price,
   * two consecutive last prices ("doubleLast" — one stray print doesn't sell), the bid/ask, or the midpoint.
   */
  trigger: z.enum(['default', 'last', 'doubleLast', 'bidAsk', 'mid']).optional(),
})
export type StopLossTier = z.infer<typeof StopLossTierSchema>
export type StopTrigger = NonNullable<StopLossTier['trigger']>
/** IBKR's triggerMethod codes. */
export const TRIGGER_METHOD: Record<StopTrigger, number> = { default: 0, last: 2, doubleLast: 3, bidAsk: 4, mid: 8 }

/** IBKR-native order conditions (evaluated by IBKR, independent of our server). */
export const NativeConditionSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('price'), instrument: InstrumentSchema, op: Cmp, value: Price }),
  /** % change vs the prior close. */
  z.object({ kind: z.literal('percentChange'), instrument: InstrumentSchema, op: Cmp, value: z.number().finite().min(-100).max(1000) }),
  z.object({ kind: z.literal('volume'), instrument: InstrumentSchema, op: Cmp, value: z.number().int().positive() }),
  z.object({ kind: z.literal('time'), op: Cmp, at: EtDateTimeSchema }),
  /** Fires when any trade in `symbol` executes in this account. */
  z.object({ kind: z.literal('execution'), symbol: SymbolSchema }),
  /** Account margin cushion, percent. */
  z.object({ kind: z.literal('margin'), op: Cmp, value: z.number().min(0).max(100) }),
])
export type NativeCondition = z.infer<typeof NativeConditionSchema>

/**
 * An additional entry tier ("kademeli giriş"): buy more lower (LMT below the first entry —
 * dip buying) or higher (STP / STP LMT above it — pyramiding). Every tier must sit on the
 * profitable side of the nearest stop, so it fills before any stop can trigger.
 */
export const EntryAddSchema = z.object({
  orderType: z.enum(['LMT', 'STP', 'STP LMT']),
  limitPrice: Price.optional(),
  stopPrice: Price.optional(),
  qty: TierQtySchema,
  /** Extra IBKR conditions for THIS tier, ANDed with the entry's shared conditions. */
  conditions: z.array(NativeConditionSchema).max(6).default([]),
})
export type EntryAdd = z.infer<typeof EntryAddSchema>

/** How long a buy zone is watched: N regular sessions (the one it starts in is session 1), until a time, or until stopped. */
export const BandWindowSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('sessions'), count: z.number().int().min(1).max(60) }),
  z.object({ kind: z.literal('until'), at: EtDateTimeSchema }),
  z.object({ kind: z.literal('gtc') }),
])
export type BandWindow = z.infer<typeof BandWindowSchema>
/**
 * What the worker does while the price is outside the zone: keep watching quietly, tell the user (once a
 * day per side), no more buys for the rest of that session (the next session re-arms), pause and ask
 * the user, or cancel for good.
 */
export const BandSideSchema = z.enum(['wait', 'notify', 'skipDay', 'pause', 'cancel'])
export type BandSide = z.infer<typeof BandSideSchema>
/** The buy limit's margin over the price (a marketable limit): auto = ⅓ of the stock's 5-minute σ, or cents, or percent. */
export const BandMarginSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('auto') }),
  z.object({ kind: z.literal('usd'), usd: z.number().positive().max(100) }),
  z.object({ kind: z.literal('pct'), pct: z.number().positive().max(5) }),
])
export type BandMargin = z.infer<typeof BandMarginSchema>

/**
 * A buy zone ("alım aralığı") instead of a fixed entry price: our engine watches the price live and
 * sends the brackets to IBKR only while it is inside [min, max] — as a limit at the price plus a small
 * margin, never beyond the zone. Below the zone (above it for a short) the orders are pulled, so
 * nothing is bought there; on the other side they stay but can only fill back inside the zone. Every
 * placement is a DAY order: nothing sits at IBKR overnight. Size and risk are computed at the zone's
 * worst price. Fields left out come from the user's Settings.
 */
export const EntryBandSchema = z.object({
  min: Price,
  max: Price,
  window: BandWindowSchema.optional(),
  /** Price under the zone. */
  below: BandSideSchema.optional(),
  /** Price over the zone. */
  above: BandSideSchema.optional(),
  /** Thesis level (a long: under min, a short: over max): once the price gets there the entry is cancelled for good. */
  invalid: Price.optional(),
  /** Don't buy (or judge) in the first N minutes of the regular session. */
  skipOpenMin: z.number().int().min(0).max(120).optional(),
  /** No new buys in the last N minutes of each session. */
  stopBeforeCloseMin: z.number().int().min(0).max(120).optional(),
  /** "5m": cancelling (thesis level, a side set to cancel) and notices count on 5-minute closes, so a wick can't cancel the plan. */
  confirm: z.enum(['live', '5m']).optional(),
  margin: BandMarginSchema.optional(),
  /**
   * No chasing (a long: over max): once a session trades at or beyond this, no buys for the rest of that
   * session even if the price comes back into the zone — the next session re-arms.
   */
  chase: Price.optional(),
  /** The pre-market gate: the last pre-market trade decides the day (thesis level, the sides, the chase level) — buying stays in regular hours. */
  premarket: z.boolean().optional(),
  /** At each session's close without a buy that day: a short note (the price, sessions left). */
  dailyNote: z.boolean().optional(),
  /**
   * Don't catch a falling price: inside the zone, buy only once the price has come back this far from
   * the session's low (a short: down from its high) — USD, % or daily ATR 14 multiples, exactly one.
   */
  bounce: z
    .object({ usd: z.number().positive().max(1_000).optional(), pct: z.number().positive().max(20).optional(), atr: z.number().positive().max(5).optional() })
    .refine((o) => [o.usd, o.pct, o.atr].filter((x) => x != null).length === 1, 'bounce: give exactly one of usd | pct | atr')
    .optional(),
})
export type EntryBand = z.infer<typeof EntryBandSchema>
/** A buy zone with every choice resolved (the spec's own, else the user's Settings). */
export interface BandSetting {
  window: BandWindow
  below: BandSide
  above: BandSide
  skipOpenMin: number
  stopBeforeCloseMin: number
  confirm: 'live' | '5m'
  margin: BandMargin
  premarket: boolean
  dailyNote: boolean
}
export const DEFAULT_BAND: BandSetting = {
  window: { kind: 'sessions', count: 3 },
  below: 'notify',
  above: 'notify',
  skipOpenMin: 0,
  stopBeforeCloseMin: 0,
  confirm: 'live',
  margin: { kind: 'auto' },
  premarket: false,
  dailyNote: true,
}
/** The buy zone of a compiled plan. */
export interface BandPlan extends BandSetting {
  min: number
  max: number
  invalid: number | null
  chase: number | null
  /** The bounce off the session's low it waits for, in dollars (null: buys at once inside the zone). */
  bounceUsd: number | null
  /** The margin in dollars (auto: ⅓ of the 5-minute σ at draft; pct: of the zone's top) — at least a cent. */
  marginUsd: number
}

export const EntrySchema = z.object({
  orderType: z.enum(['LMT', 'MKT', 'STP', 'STP LMT']),
  limitPrice: Price.optional(),
  stopPrice: Price.optional(),
  tif: z.enum(['DAY', 'GTC', 'GTD']).default('GTC'),
  /** Required for GTD: the entry is cancelled at this time if not filled. */
  goodTill: EtDateTimeSchema.optional(),
  outsideRth: z.boolean().default(false),
  /** The entry is only submitted once these hold (IBKR-side). */
  conditions: z.array(NativeConditionSchema).max(6).default([]),
  conditionsLogic: z.enum(['and', 'or']).default('and'),
  /** More entry tiers. The first entry (above) takes whatever the adds leave of the size. */
  adds: z.array(EntryAddSchema).max(5).default([]),
  /** A buy zone watched live instead of a fixed limit (orderType LMT; the limit is set from the zone). */
  band: EntryBandSchema.optional(),
})
export type EntrySpec = z.infer<typeof EntrySchema>

export const SizingSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('shares'), shares: z.number().positive().max(100_000) }),
  /** Buy as many whole shares as fit in `usd` at the entry price. */
  z.object({ kind: z.literal('notional'), usd: z.number().positive().max(100_000_000) }),
  /** Size so the planned stop-out loss is at most `usd`. */
  z.object({ kind: z.literal('risk'), usd: z.number().positive().max(10_000_000) }),
  /** Buy `pct`% of the account's net liquidation (IBKR value at draft time). */
  z.object({ kind: z.literal('accountPct'), pct: z.number().positive().max(100) }),
  /** Size so the planned stop-out loss is at most `pct`% of net liquidation. */
  z.object({ kind: z.literal('riskPct'), pct: z.number().positive().max(100) }),
])
export type Sizing = z.infer<typeof SizingSchema>

/**
 * A market indicator on IBKR TRADES bars (regular hours) of this worker's stock or any other
 * stock/index. `price` = the last price (live) or a bar close; `high`/`low` = the highest high /
 * lowest low of the last `period` COMPLETED bars; `vwap` = today's session VWAP (timeframe
 * ignored); `rvol` = today's volume vs the `period`-session average (1d only, time-adjusted live).
 */
export const IndicatorSchema = z.object({
  /**
   * …plus `open` / `sessionHigh` / `sessionLow` = today's regular session so far (live; a completed day
   * in close mode) and `entry` = this worker's average entry fill (its own stock only, no period).
   */
  kind: z.enum([
    'price',
    'sma',
    'ema',
    'rsi',
    'atr',
    'vwap',
    'rvol',
    'high',
    'low',
    'open',
    'sessionHigh',
    'sessionLow',
    'entry',
    /** Today's % change vs the prior close (e.g. against a sector ETF's). */
    'change',
    /** VWAP anchored at `anchor` (a date, e.g. the panic day), from daily bars. */
    'avwap',
    /** The high / low of the first `period` minutes of today's session (the opening range). */
    'orHigh',
    'orLow',
  ]),
  period: z.number().int().min(1).max(250).optional(),
  timeframe: z.enum(['5m', '15m', '1h', '1d']).default('1d'),
  instrument: InstrumentSchema.optional(),
  /** avwap: the first day (YYYY-MM-DD) the VWAP counts from. */
  anchor: z
    .string()
    .trim()
    .refine((x) => /^\d{4}-\d{2}-\d{2}$/.test(x), 'expected "YYYY-MM-DD"')
    .optional(),
})
export type Indicator = z.infer<typeof IndicatorSchema>

/** A signed distance: USD, % of the value, or multiples of the stock's daily ATR 14 — exactly one. */
export const OffsetSchema = z
  .object({
    usd: z.number().finite().min(-10_000).max(10_000).optional(),
    pct: z.number().finite().min(-50).max(50).optional(),
    atr: z.number().finite().min(-10).max(10).optional(),
  })
  .refine((o) => [o.usd, o.pct, o.atr].filter((x) => x != null).length === 1, 'offset: give exactly one of usd | pct | atr')
export type Offset = z.infer<typeof OffsetSchema>

/**
 * A price worked out live (an "expression price"): an indicator — the session's open / high / low,
 * VWAP, an SMA, the N-bar low, the average entry… — plus a signed offset. E.g. SMA 200 − 0.5 ATR =
 * {"of":{"kind":"sma","period":200},"offset":{"atr":-0.5}}; the 3-day low − 1¢ = {"of":{"kind":"low","period":3},"offset":{"usd":-0.01}}.
 */
export const PriceExprSchema = z.object({ of: IndicatorSchema, offset: OffsetSchema.optional() })
export type PriceExpr = z.infer<typeof PriceExprSchema>

/** The other side of an indicator comparison: a number, another indicator, or an expression price. */
export const OperandSchema = z.union([z.number(), IndicatorSchema, PriceExprSchema])
export type Operand = z.infer<typeof OperandSchema>

export const isExpr = (o: unknown): o is PriceExpr => typeof o === 'object' && o !== null && 'of' in o
/** The daily ATR 14 an expression's `atr` offset is measured in (of the expression's own instrument). */
export const atr14Of = (x: PriceExpr): Indicator => ({ kind: 'atr', period: 14, timeframe: '1d', ...(x.of.instrument ? { instrument: x.of.instrument } : {}) })
/** Every indicator an operand reads (an expression with an ATR offset reads the daily ATR 14 too). */
export function operandIndicators(o: Operand | PriceExpr): Indicator[] {
  if (typeof o === 'number') return []
  if (!isExpr(o)) return [o]
  return o.offset?.atr != null ? [o.of, atr14Of(o)] : [o.of]
}

export const WORKER_EVENTS = ['activated', 'entryFilled', 'tookProfit', 'stoppedOut', 'closed'] as const
export const WorkerEventKindSchema = z.enum(WORKER_EVENTS)
export type WorkerEventKind = z.infer<typeof WorkerEventKindSchema>

/** What makes a rule fire. Fill triggers fire on the FIRST execution unless `complete`. */
export const TriggerSchema = z.discriminatedUnion('on', [
  z.object({
    on: z.literal('fill'),
    what: z.enum(['entry', 'tp', 'sl']),
    /** tp/sl tier (1-based); omitted = any tier of that ladder. */
    tier: TierNo.optional(),
    /** Wait until the whole entry / tier has filled instead of the first execution. */
    complete: z.boolean().default(false),
  }),
  /** Price of this worker's symbol (default) or any other stock/index crosses `value`. */
  z.object({ on: z.literal('price'), instrument: InstrumentSchema.optional(), op: Cmp, value: Price }),
  z.object({ on: z.literal('time'), at: EtDateTimeSchema }),
  z.object({
    on: z.literal('elapsed'),
    /** tookProfit = the first take-profit fill. */
    since: z.enum(['activated', 'entryFilled', 'tookProfit']),
    minutes: z.number().int().positive().max(525_600),
  }),
  /** Another worker's lifecycle event (cross-worker workflows). */
  z.object({ on: z.literal('worker'), workerId: z.string().trim().min(1).max(40), event: WorkerEventKindSchema }),
  /**
   * An indicator condition. 'live' judges every update; 'close' judges only completed bars of
   * `left`'s timeframe that close after the rule is armed (e.g. "a daily close above SMA 200").
   */
  z.object({
    on: z.literal('indicator'),
    left: IndicatorSchema,
    op: z.enum(['>=', '<=', 'crossAbove', 'crossBelow'], {
      required_error: 'indicator trigger needs op: ">=", "<=", "crossAbove" or "crossBelow" — e.g. {"on":"indicator","left":{"kind":"rsi","period":14},"op":"<=","right":30}',
    }),
    right: OperandSchema,
    at: z.enum(['live', 'close']).default('live'),
  }),
  /** The open or close of the Nth regular session, counting the session of the base event as 1. */
  z.object({
    on: z.literal('sessions'),
    since: z.enum(['activated', 'entryFilled', 'tookProfit'], { required_error: 'sessions trigger needs since: "activated", "entryFilled" or "tookProfit"' }),
    count: z.number({ required_error: 'sessions trigger needs count (1–250)' }).int().min(1).max(250),
    at: z.enum(['open', 'close']).default('close'),
    /** Minutes after that open (e.g. 15 = the open + 15 min), or before that close with a negative number. */
    offsetMin: z.number().int().min(-390).max(390).optional(),
  }),
  /**
   * Around the next earnings report (date fixed on the card at draft time): 'before' = 15 min
   * before the close of the last session before the report (minus `sessions` more);
   * 'after' = the open of the first session after it.
   */
  z.object({
    on: z.literal('earnings'),
    timing: z.enum(['before', 'after'], { required_error: 'earnings trigger needs timing: "before" or "after"' }),
    sessionsEarlier: z.number().int().min(0).max(3).default(0),
  }),
  /** Every session at or after this US/Eastern time ("HH:MM"), until that session's close — e.g. judge the day at 15:45. */
  z.object({
    on: z.literal('daily'),
    at: z
      .string()
      .trim()
      .refine((x) => /^([01]\d|2[0-3]):[0-5]\d$/.test(x), 'expected "HH:MM" (US/Eastern)'),
  }),
  /**
   * The opening gap: a regular session (one that opens after the rule is armed) opens at or below /
   * at or above `value`, or `pct`% vs the prior close (e.g. pct -3 with "<=" = opens 3% or more down).
   */
  z.object({
    on: z.literal('open'),
    op: Cmp,
    value: Price.optional(),
    pct: z.number().finite().min(-90).max(500).optional(),
  }),
])
export type Trigger = z.infer<typeof TriggerSchema>

/** Extra conditions checked at the moment the trigger fires (all must hold). */
export const GuardSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('filled'), what: z.enum(['entry', 'tp', 'sl']), tier: TierNo.optional(), is: z.boolean() }),
  z.object({ kind: z.literal('price'), instrument: InstrumentSchema.optional(), op: Cmp, value: Price }),
  z.object({ kind: z.literal('position'), is: z.enum(['open', 'flat']) }),
  /**
   * An indicator comparison when the trigger fires: live values, or `at: "close"` = the last completed
   * bar of left's timeframe. `ever` = it has held at least once since the worker started (e.g. "a daily
   * close above SMA 200 happened"); `not` = it must NOT hold (with ever: never happened).
   */
  z.object({
    kind: z.literal('indicator'),
    left: IndicatorSchema,
    op: z.enum(['>=', '<='], { required_error: 'indicator guard needs op: ">=" or "<="' }),
    right: OperandSchema,
    at: z.enum(['live', 'close']).default('live'),
    ever: z.boolean().default(false),
    not: z.boolean().default(false),
  }),
])
export type Guard = z.infer<typeof GuardSchema>

export const ActionSchema = z.discriminatedUnion('do', [
  /**
   * Tighten stops (never loosens — a looser target is skipped). `breakeven` = average entry fill; an
   * expression price is worked out when the rule fires (skipped if it is at or through the price).
   */
  z.object({
    do: z.literal('moveStop'),
    to: z.union([Price, z.literal('breakeven'), PriceExprSchema]),
    tiers: z.array(TierNo).max(6).optional(),
    /** Ask first: the move becomes a change card the user approves (Telegram link) instead of happening at once. */
    confirm: z.boolean().optional(),
  }),
  z.object({ do: z.literal('moveTarget'), tier: TierNo, to: Price }),
  /** Cancel whatever part of the entry has not filled yet. */
  z.object({ do: z.literal('cancelEntry') }),
  /**
   * Cancel all remaining orders and exit the open position at market — with `auction`, in the closing
   * auction (market-on-close) when it is still before 15:50 ET, else at market.
   */
  z.object({ do: z.literal('close'), auction: z.boolean().optional() }),
  /**
   * Take part of the position off at market: `percent`% of the open shares (rounded, at least
   * one) or exactly `shares`. Lots with the widest stops go first; a lot that is only partly
   * reduced keeps its stop and target for the shares that stay.
   */
  z.object({ do: z.literal('reduce'), percent: z.number().positive().max(100).optional(), shares: z.number().positive().optional() }),
  z.object({ do: z.literal('notify'), message: z.string().trim().min(1).max(300) }),
  /**
   * A checkpoint: tell the user and pause the worker — no more rules, no new buys from a buy zone —
   * until they resume or change it. Its stops and targets stay at IBKR the whole time.
   */
  z.object({ do: z.literal('pause'), message: z.string().trim().min(1).max(300) }),
  /**
   * Call back the AI that planned this worker (in its chat) with this question. It researches (news,
   * the price, the plan) and may propose a change card — a new stop, a target, "close now" — that the
   * user approves from Telegram; it never changes anything itself.
   */
  z.object({ do: z.literal('askAI'), message: z.string().trim().min(1).max(500) }),
  /**
   * From now on our engine trails the stops (all, or these tiers) at this distance under the highest
   * price since the rule fired (a short: over the lowest) — USD, % or daily ATR 14 multiples; it only
   * tightens, and IBKR keeps the last level if our server stops.
   */
  z.object({
    do: z.literal('trail'),
    usd: z.number().positive().max(10_000).optional(),
    pct: z.number().positive().max(50).optional(),
    atr: z.number().positive().max(10).optional(),
    tiers: z.array(TierNo).max(6).optional(),
  }),
])
export type Action = z.infer<typeof ActionSchema>

export const RuleSchema = z.object({
  id: z.string().trim().regex(/^[A-Za-z0-9_-]{1,32}$/, 'rule id: letters, digits, _ or -'),
  when: TriggerSchema,
  if: z.array(GuardSchema).max(6).default([]),
  then: z.array(ActionSchema).min(1).max(8),
})
export type Rule = z.infer<typeof RuleSchema>

export const StartSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('onApproval') }),
  /** Stay armed until another worker emits `event`, then place the orders. */
  z.object({ kind: z.literal('afterWorker'), workerId: z.string().trim().min(1).max(40), event: WorkerEventKindSchema }),
  /** Stay armed until a market condition holds (price / indicator / time / earnings), then place the orders. */
  z.object({ kind: z.literal('when'), trigger: TriggerSchema }),
])

/**
 * Insurance ("sigorta"): once the price has covered `level`% of the way from a lot's stop to its
 * target, IBKR moves that lot's stop to its fee-covered break-even (entry + both commissions +
 * 1¢) — a trade that came close to its target can no longer end at the original stop. It runs at
 * IBKR (an adjusted stop), so it works while our server is down. Left out of a spec → the user's
 * Settings default, written into the worker when it is drafted.
 */
export const InsuranceSchema = z.object({
  /** false = no insurance step for this worker. */
  on: z.boolean(),
  /** Percent of the stop→target way that arms it (50–99): 95 ≈ free, 90 small cost, 75 strongest and costliest. Left out → Settings. */
  level: z.number().int().min(50).max(99).optional(),
  /**
   * Which target the level is measured to: 'own' = each lot's own target (the tested default);
   * 'first' = the plan's nearest target for every lot, so nearing TP1 insures all lots at once
   * (more certainty, fewer later lots reach their own targets).
   */
  basis: z.enum(['own', 'first']).optional(),
  /** When a lot takes its profit, every other open lot's stop moves to its fee-covered break-even. Left out → Settings (off with `on: false`). */
  protectOthers: z.boolean().optional(),
  /**
   * The user's own insurance, "takılırsa hedefe in": the stop stays where it is. Near a lot's target
   * (the zone) the price may hover — normal for this stock's own 5-minute noise σ for a while. The
   * wait is how long pure noise would need to cover the rest with `confidence`% probability:
   * n = (d / (σ·z))² five-minute bars, z = Φ⁻¹(1 − p/2). If the target still has not filled after that
   * much trading time, the hover is not noise any more → the target drops to the zone and sells there.
   * Runs in our engine on IBKR quotes. Tested best: zone 1σ under the target, 70–80% confidence.
   */
  stall: z
    .object({
      on: z.boolean(),
      /** Zone = this many σ (5-minute noise) under the target (default 1). */
      sigmas: z.number().min(0.5).max(5).optional(),
      /** Or: zone at this % of the stop → target way (replaces sigmas). */
      way: z.number().int().min(50).max(99).optional(),
      /** How sure noise would have reached the target by the end of the wait (default 80). */
      confidence: z.union([z.literal(50), z.literal(60), z.literal(70), z.literal(80), z.literal(90)]).optional(),
      /** A fixed wait in trading minutes instead of the formula. */
      minutes: z.number().int().min(5).max(1950).optional(),
    })
    .optional(),
})
export type Insurance = z.infer<typeof InsuranceSchema>
/** Fully resolved insurance of a plan. */
export interface InsuranceSetting {
  /** The break-even step at IBKR. */
  on: boolean
  level: number
  basis: 'own' | 'first'
  protectOthers: boolean
  /** "Stalls near the target → the target drops to the zone" (engine-side). Absent on plans from before it. */
  stall?: { on: boolean; sigmas: number; way: number | null; confidence: 50 | 60 | 70 | 80 | 90; minutes: number | null }
}
// Off by default (2026-09-27 tests: on panic-drop bounces with targets at real resistances every guard gave up more
// winners than it saved losers); each worker can take either or both.
export const DEFAULT_INSURANCE: InsuranceSetting = {
  on: false,
  level: 90,
  basis: 'own',
  protectOthers: false,
  stall: { on: false, sigmas: 1, way: null, confidence: 80, minutes: null },
}

export const WorkerSpecSchema = z.object({
  version: z.literal(WORKER_SPEC_VERSION).default(WORKER_SPEC_VERSION),
  name: z.string().trim().min(1).max(80),
  symbol: SymbolSchema,
  side: z.enum(['long', 'short']),
  sizing: SizingSchema,
  entry: EntrySchema,
  /** Nearest target first. May cover less than the position (the rest is a runner). */
  takeProfits: z.array(TakeProfitTierSchema).max(6).default([]),
  /** Nearest stop first. Must cover the whole position — unless `noStop`. */
  stopLosses: z.array(StopLossTierSchema).max(6).default([]),
  /**
   * No price stop — the user's explicit choice for this trade (a long only): the lots carry no stop at
   * IBKR, the most that can be lost is the whole position, and the notional cap (Settings → risk limits,
   * always on) is the one hard limit. A rule can still place a stop later (moveStop).
   */
  noStop: z.boolean().default(false),
  /** Let the exit orders trigger/fill in pre- and post-market too. */
  exitOutsideRth: z.boolean().default(false),
  rules: z.array(RuleSchema).max(24).default([]),
  insurance: InsuranceSchema.optional(),
  start: StartSchema.default({ kind: 'onApproval' }),
  /** After each session's close the AI reviews this worker and may propose a change card (the user approves it). */
  review: z.object({ sessionEnd: z.boolean() }).optional(),
  /** Free-text rationale from the chat. Informational only — never executed. */
  note: z.string().trim().max(1500).optional(),
  /**
   * Manage shares the user ALREADY holds: no buy is sent — the worker takes over `sizing.shares` of the
   * position (bought at `avgCost` on average) and places their stops, targets and rules. The entry is
   * a market entry on paper only: prices are measured from the price at the moment the card is drawn.
   */
  adopt: z.object({ avgCost: Price }).optional(),
  /** The broker this worker trades at (written at draft; a worker from before Robinhood has none = IBKR). */
  broker: z.enum(BROKER_NAMES).optional(),
})
export type WorkerSpec = z.infer<typeof WorkerSpecSchema>
export type WorkerSpecInput = z.input<typeof WorkerSpecSchema>

/* ── compiled plan (exact broker orders) ─────────────────────────────── */

export type PlanOrderType = 'MKT' | 'LMT' | 'STP' | 'STP LMT' | 'TRAIL'

export interface PlanLot {
  lot: number
  qty: number
  /** Which entry tier buys this lot (1 = the first entry, 2.. = adds) and at what order. */
  entry: { tier: number; orderType: 'LMT' | 'MKT' | 'STP' | 'STP LMT'; limitPrice: number | null; stopPrice: number | null; ref: number }
  tp: { tier: number; price: number } | null
  sl: {
    tier: number
    stop: number
    limit: number | null
    trail: { amount: number | null; percent: number | null } | null
    adjust: { trigger: number; stop: number } | null
    /** How IBKR triggers the stop (absent: its default). */
    trigger?: StopTrigger
  }
  /** Insurance for this lot (absent on plans from before insurance, or with insurance and protectOthers both off). */
  insurance?: LotInsurance
  /** No stop at IBKR for this lot (a no-stop worker): `sl` is a placeholder (stop 0) until a rule places one. */
  noStop?: true
}

export interface LotInsurance {
  /** Commissions of the lot's entry and exit per share, plus 1¢: break-even = entry fill ± this. */
  feePerShare: number
  /** Fee-covered break-even at the planned entry: where insurance and "protect others" move the stop. */
  be: number
  /** Price that arms the IBKR adjustment to `be`; null = this lot is not insured (see `skip`). */
  trigger: number | null
  /** Why the lot is not insured (no target, trailing stop, …). */
  skip?: string
  /** Stall insurance: the zone price that starts the wait and where the target drops to (null: none for this lot). */
  stallZone?: number | null
  /** …and how many trading minutes the price may hover there before the target drops. */
  stallWaitMin?: number | null
  /** Why this lot has no stall zone. */
  stallSkip?: string
}

/** One planned stop move for a lot: where the stop goes (a price, break-even, or a level worked out when it fires) and after what. */
export interface StopStep {
  rule: string
  to: number | 'breakeven' | { text: string }
  after: string
}

const pxText = (n: number) => (Math.abs(n) >= 1 ? n.toFixed(2) : n.toFixed(4))
const durText = (min: number) => (min % 1440 === 0 ? `${min / 1440}d` : min % 60 === 0 ? `${min / 60}h` : `${min}m`)

const TF_SHORT: Record<Indicator['timeframe'], string> = { '5m': '5m', '15m': '15m', '1h': '1h', '1d': '' }
/** Compact indicator name for lot tables and Telegram: "SMA200", "3-bar low", "session low", "entry". */
export function indicatorShort(i: Indicator): string {
  const tf = TF_SHORT[i.timeframe] ? ` ${TF_SHORT[i.timeframe]}` : ''
  const sym = i.instrument ? `${i.instrument.symbol} ` : ''
  switch (i.kind) {
    case 'price':
      return `${sym}price`
    case 'entry':
      return 'entry'
    case 'open':
      return `${sym}session open`
    case 'sessionHigh':
      return `${sym}session high`
    case 'sessionLow':
      return `${sym}session low`
    case 'vwap':
      return `${sym}VWAP`
    case 'change':
      return `${sym}% change today`
    case 'avwap':
      return `${sym}VWAP since ${i.anchor ?? '?'}`
    case 'orHigh':
    case 'orLow':
      return `${sym}${i.period ?? 30}-min opening range ${i.kind === 'orHigh' ? 'high' : 'low'}`
    case 'high':
    case 'low':
      return `${sym}${i.period ?? 1}-bar${tf} ${i.kind}`
    default:
      return `${sym}${i.kind.toUpperCase()}${i.period ?? ''}${tf}`
  }
}
/** "SMA200 − 0.5 ATR", "3-bar low − $0.05", "entry + 1%". */
export function exprText(x: PriceExpr): string {
  const o = x.offset
  if (!o) return indicatorShort(x.of)
  const v = o.usd ?? o.pct ?? o.atr ?? 0
  const unitless = x.of.kind === 'rsi' || x.of.kind === 'change' || x.of.kind === 'rvol'
  const amt = o.usd != null ? (unitless ? `${Math.abs(v)} pts` : `$${pxText(Math.abs(v))}`) : o.pct != null ? `${Math.abs(v)}%` : `${Math.abs(v)} ATR`
  return `${indicatorShort(x.of)} ${v < 0 ? '−' : '+'} ${amt}`
}

function afterText(t: Trigger, spec: WorkerSpec): string {
  switch (t.on) {
    case 'fill':
      if (t.what === 'entry') return t.tier && t.tier > 1 ? `after add ${t.tier - 1}` : 'after the entry'
      return `after ${t.what.toUpperCase()}${t.tier ?? ''}`
    case 'price':
      return `at ${t.instrument && t.instrument.symbol !== spec.symbol ? `${t.instrument.symbol} ` : ''}${t.op === '>=' ? '≥' : '≤'} ${pxText(t.value)}`
    case 'time':
      return `at ${t.at} ET`
    case 'elapsed':
      return `${durText(t.minutes)} after ${t.since === 'activated' ? 'placing' : t.since === 'tookProfit' ? 'the first target' : 'the entry'}`
    case 'worker':
      return `on worker ${t.workerId} ${t.event}`
    case 'indicator': {
      const op = { '>=': '≥', '<=': '≤', crossAbove: 'crosses above', crossBelow: 'crosses below' }[t.op]
      const r = t.right
      return `when ${indicatorShort(t.left)} ${op} ${typeof r === 'number' ? r : 'of' in r ? exprText(r) : indicatorShort(r)}`
    }
    case 'sessions':
      return `after ${t.count} session${t.count === 1 ? '' : 's'}`
    case 'earnings':
      return `${t.timing} earnings`
    case 'open':
      return `if a session opens ${t.op === '>=' ? '≥' : '≤'} ${t.value != null ? pxText(t.value) : `${t.pct}%`}`
    case 'daily':
      return `each session from ${t.at} ET`
  }
}

/**
 * The stop moves a lot's rules plan for it, in the order the price would reach them — so a card
 * can show "97.00 → 102.00 after TP1 → 103.00 after TP2" instead of only the starting stop.
 * Leaves out moves for lots that have exited by then (their own target) and moves that would not
 * tighten (the engine never loosens a stop). With the plan it also shows the insurance step and
 * "protect others" (another lot's target fills → this lot's stop to its break-even).
 */
export function stopPath(spec: WorkerSpec, lot: PlanLot, plan?: Pick<WorkerPlan, 'lots' | 'insurance'>): StopStep[] {
  const ins = lot.insurance
  if (lot.sl.trail || (lot.sl.adjust && ins?.trigger == null)) return [] // those move at IBKR on their own
  const long = spec.side === 'long'
  const tpPrice = (tier: number) => spec.takeProfits[tier - 1]?.price
  // Rough price at which a trigger happens, to order the moves as the trade develops.
  const level = (t: Trigger): number | null => {
    if (t.on === 'fill' && t.what === 'tp' && t.tier != null) return tpPrice(t.tier) ?? null
    if (t.on === 'price' && (!t.instrument || t.instrument.symbol === spec.symbol)) return t.value
    return null
  }
  const steps: { step: StopStep; key: number; i: number }[] = []
  ;(spec.rules ?? []).forEach((r, i) => {
    const t = r.when
    if (t.on === 'fill' && t.tier != null) {
      if (t.what === 'tp' && lot.tp && lot.tp.tier <= t.tier) return // this lot took its profit by then
      if (t.what === 'sl' && lot.sl.tier <= t.tier) return // … or was stopped out
    }
    const lv = level(t)
    for (const a of r.then) {
      if (a.do !== 'moveStop' || (a.tiers?.length && !a.tiers.includes(lot.sl.tier))) continue
      const to = typeof a.to === 'object' ? { text: exprText(a.to) } : a.to
      steps.push({ step: { rule: r.id, to, after: afterText(t, spec) }, key: lv == null ? Infinity : long ? lv : -lv, i })
    }
  })
  if (ins?.trigger != null)
    steps.push({ step: { rule: 'insurance', to: ins.be, after: `at ${pxText(ins.trigger)} (insurance)` }, key: long ? ins.trigger : -ins.trigger, i: -2 })
  if (ins && plan?.insurance?.protectOthers) {
    // The nearest target of ANOTHER lot that fills while this one is still open.
    const nearer = plan.lots.filter((o) => o.lot !== lot.lot && o.tp && (!lot.tp || (long ? o.tp.price < lot.tp.price : o.tp.price > lot.tp.price)))
    const first = nearer.sort((a, b) => (long ? a.tp!.price - b.tp!.price : b.tp!.price - a.tp!.price))[0]
    if (first) steps.push({ step: { rule: 'protect', to: ins.be, after: `after TP${first.tp!.tier} fills (another lot)` }, key: long ? first.tp!.price : -first.tp!.price, i: -1 })
  }
  steps.sort((a, b) => a.key - b.key || a.i - b.i)
  const out: StopStep[] = []
  let cur = lot.sl.stop
  for (const { step } of steps) {
    if (typeof step.to === 'number') {
      if (!(long ? step.to > cur : step.to < cur)) continue
      cur = step.to
    }
    out.push(step)
  }
  return out
}

export interface PlanOrder {
  /** Stable key inside the plan, e.g. "L2.SL". */
  key: string
  lot: number
  role: 'entry' | 'tp' | 'sl'
  tier: number | null
  action: 'BUY' | 'SELL'
  orderType: PlanOrderType
  qty: number
  limitPrice: number | null
  stopPrice: number | null
  trailAmount: number | null
  trailPercent: number | null
  adjust: { trigger: number; stop: number } | null
  /** IBKR triggerMethod of a stop (absent: its default). */
  triggerMethod?: number
  tif: 'DAY' | 'GTC' | 'GTD'
  goodTill: string | null
  outsideRth: boolean
  parentKey: string | null
  conditions: NativeCondition[]
  conditionsLogic: 'and' | 'or'
}

/** The next earnings report of the worker's stock, fixed at draft time (shown on the card). */
export interface EarningsDate {
  /** YYYY-MM-DD (US/Eastern). */
  date: string
  /** Before the open, after the close, or unknown. */
  when: 'bmo' | 'amc' | null
  source: 'EODHD'
}

export interface WorkerPlan {
  version: number
  symbol: string
  side: 'long' | 'short'
  /** Next earnings (null = none found; absent on plans from before earnings support). */
  earnings?: EarningsDate | null
  quantity: number
  /** Price used for sizing and risk: the limit/stop of the entry, or the live quote for MKT. */
  entryRef: number
  entryRefSource: 'limit' | 'stop' | 'quote'
  tpShares: number[]
  slShares: number[]
  lots: PlanLot[]
  orders: PlanOrder[]
  /** Resolved insurance (absent on plans from before insurance). */
  insurance?: InsuranceSetting
  /** A buy zone: the entry orders go to IBKR only while the price is inside it (their limit is set then). */
  band?: BandPlan
}

export interface WorkerScenario {
  label: string
  /** Realized P&L in USD before commissions. */
  pnl: number
  /** Estimated IBKR commissions + pass-through fees of the orders that fill on this path. */
  fees?: number
  /** pnl − fees. */
  net?: number
  detail: string
  /** The same path without insurance / "protect others" — only when that changes the result. */
  withoutInsurance?: { net: number; detail: string }
}

/** Fee estimate for a worker card (shared/fees.ts). */
export interface WorkerFees {
  plan: CommissionPlan
  /** Every entry + every stop filling — the max-loss path. */
  atStops: number
  /** Every entry + every target filling; null when some shares have no target. */
  atTargets: number | null
  /** Orders on that path that pay the plan's per-order minimum, of `orders`. */
  ordersAtMin: number
  orders: number
  /** The same paths on the other IBKR Pro plan, for comparison. */
  other: { plan: CommissionPlan; atStops: number; atTargets: number | null }
}

/** Computed by the system from the plan — never written by the AI. */
export interface WorkerSummary {
  headline: string
  /** How the share count was derived (e.g. "2% of $1,026,540 net liquidation → 6 sh"). */
  sizingText?: string
  /** With entry tiers: the average cost if every tier fills. */
  avgEntryIfAllFill?: number
  quantity: number
  entryRef: number
  notional: number
  /** Loss if every stop fills at its price (positive number, USD). */
  maxLoss: number
  /** Profit if every take-profit fills; null when some shares have no target (runner). */
  bestCase: number | null
  rewardRisk: number | null
  /** After estimated fees (absent on cards drawn before fees were shown). */
  fees?: WorkerFees
  maxLossNet?: number
  bestCaseNet?: number | null
  rewardRiskNet?: number | null
  lotsText: string[]
  ordersText: string[]
  conditionsText: string[]
  rulesText: { id: string; text: string }[]
  scenarios: WorkerScenario[]
  /** Insurance of this worker in plain words (absent on cards drawn before insurance). */
  insurance?: { on: boolean; level: number; protectOthers: boolean; lines: string[] }
  /** The buy zone in plain words. */
  band?: { lines: string[] }
  /** Why each price is where it is, against the stock's own profile (IBKR bars), and what looks off. */
  basis?: { lines: string[]; warnings: string[] }
  /** Prices / conditions the note mentions that no order, zone or rule uses (the note is never executed). */
  noteIssues?: string[]
  /** Set on a change card of a running worker. */
  revision?: RevisionSummary
  warnings: string[]
  /** Indicator values at draft time per rule (id) or the 'when' start (id "start"). */
  marketNow?: { id: string; text: string }[]
}

export interface RiskCheck {
  id: string
  label: string
  ok: boolean
  detail: string
  /** 'info' rows are shown for awareness and never block approval (e.g. risk limits switched off). */
  level?: 'block' | 'info'
}

export interface BrokerPreview {
  commission: number | null
  minCommission: number | null
  maxCommission: number | null
  initMarginChange: number | null
  maintMarginChange: number | null
  equityWithLoanAfter: number | null
  warning: string | null
}

export interface LimitSetting {
  on: boolean
  pct: number
}

/**
 * Global risk limits — OFF by default (the user decides how much risk to take). When the
 * master switch is off, every limit is still computed and shown on the card, but only as
 * information; each limit can also be switched on/off on its own.
 */
export interface WorkerLimits {
  enabled: boolean
  /** A single worker's max loss ≤ X% of net liquidation. */
  maxWorkerRiskPct: LimitSetting
  /** Sum of all active workers' max losses ≤ X% of net liquidation. */
  maxOpenRiskPct: LimitSetting
  /** One symbol's exposure (IBKR position + active workers) ≤ X% of net liquidation. */
  maxSymbolExposurePct: LimitSetting
  /** Block new workers once today's P&L is below −X% of net liquidation. */
  maxDailyLossPct: LimitSetting
  /** Workers without a stop (always enforced, even with the limits off): one trade's cost, and all of them together. */
  noStopCap: { perTradeUsd: number; totalUsd: number }
}

export const DEFAULT_WORKER_LIMITS: WorkerLimits = {
  enabled: false,
  maxWorkerRiskPct: { on: true, pct: 2 },
  maxOpenRiskPct: { on: true, pct: 6 },
  maxSymbolExposurePct: { on: true, pct: 30 },
  maxDailyLossPct: { on: true, pct: 3 },
  noStopCap: { perTradeUsd: 750, totalUsd: 2_000 },
}

const LimitSettingSchema = (max: number) => z.object({ on: z.boolean(), pct: z.number().positive().max(max) })
export const WorkerLimitsSchema = z.object({
  enabled: z.boolean(),
  maxWorkerRiskPct: LimitSettingSchema(100),
  maxOpenRiskPct: LimitSettingSchema(100),
  maxSymbolExposurePct: LimitSettingSchema(500),
  maxDailyLossPct: LimitSettingSchema(100),
  noStopCap: z
    .object({ perTradeUsd: z.number().positive().max(10_000_000), totalUsd: z.number().positive().max(100_000_000) })
    .default({ perTradeUsd: 750, totalUsd: 2_000 }),
})

/* ── runtime / API shapes ────────────────────────────────────────────── */

export type WorkerStatus =
  | 'draft' // compiled, waiting for the user's approval
  | 'rejected' // the user declined the draft
  | 'expired' // the draft was not approved in time
  | 'waiting' // approved, armed until its start trigger fires
  | 'working' // orders at IBKR, nothing filled yet
  | 'open' // holds a position; exits working at IBKR
  | 'closing' // exiting at market
  | 'done' // flat, all orders finished
  | 'cancelled' // stopped before anything filled
  | 'error' // needs attention — automation paused for this worker
  | 'applied' // a change card (revision) the user approved — now part of the worker it changed

export const ACTIVE_WORKER_STATUSES: WorkerStatus[] = ['waiting', 'working', 'open', 'closing', 'error']

export interface WorkerLotState {
  lot: number
  qty: number
  entryFilled: number
  entryDone: boolean
  tpFilled: number
  slFilled: number
  closeFilled: number
  open: number
  stop: number
  target: number | null
}

export interface ReduceJob {
  /** The rule id — or "v<n>" for a change that rebuilds a lot's exits (sell 0). */
  rule: string
  lot: number
  /** A change rebuilding exits: the fresh stop and target (instead of the lot's current ones). */
  stop?: number
  target?: number | null
  /** Shares to sell at market. */
  sell: number
  /** Shares that stay, under a fresh stop (+ target) (0 = the whole lot goes). */
  keep: number
  /** cancel: the lot's exits are being cancelled at IBKR · sent: market order + fresh exits placed. */
  stage: 'cancel' | 'sent' | 'done' | 'failed'
  since: number
  orderId: number | null
}

/** Lots whose orders a change rebuilds: cancel the old bracket(s), then place the new ones (never both live). */
export interface RebuildJob {
  version: number
  /** Plan lots to place; every one of them had nothing bought. */
  lots: number[]
  /** Order ids of the old entries being cancelled. */
  cancelling: number[]
  stage: 'cancel' | 'done' | 'failed'
  since: number
}

export interface WorkerRuntime {
  lots: WorkerLotState[]
  avgEntry: number | null
  realized: number
  commissions: number
  firedRules: string[]
  emitted: WorkerEventKind[]
  activatedAt: number | null
  entryFilledAt: number | null
  closedAt: number | null
  closeReason: string | null
  closingSince: number | null
  /** Partial closes started by `reduce` rules (per lot: exits cancelled, then a market order + fresh exits for the rest). */
  reducing?: ReduceJob[]
  /** Live indicator crosses: the last side seen per rule (true = left above right). */
  crossSide?: Record<string, boolean>
  /** When the start condition began being watched (a 'when' start only counts bars after it). */
  armedAt?: number | null
  /** The take-profit execution "protect others" last acted on (it acts once per profit). */
  protectedFill?: string | null
  /** Stall insurance per lot: when the price first reached the zone, and whether the target was lowered. */
  stall?: Record<string, { at: number; done: boolean }>
  /** A change is rebuilding lots not bought yet: their old brackets are cancelled, then the new ones placed. */
  rebuilding?: RebuildJob[]
  /** The buy zone being watched (plans with a band). */
  band?: BandRuntime
  /** Guards with `ever` that have held once (key "<rule id>#<guard index>"). */
  latched?: string[]
  /** Stops our engine trails since a trail rule fired (per lot). */
  trails?: Record<string, { rule: string; dist: number; hw: number; since: number }>
  /** The close was asked for in the closing auction (market-on-close). */
  closeAuction?: boolean
  /** The ET date of the last session-end AI review. */
  reviewed?: string | null
  /** Trading in the stock is halted (IBKR): since when, and when it resumed. */
  halt?: { since: number; ended: number | null } | null
  /** Why the worker needs a human look (automation of rules is paused while set). */
  attention: string | null
}

/** Live state of a buy zone. */
export interface BandRuntime {
  /** When the zone started being watched (a sessions window counts from here). */
  since: number
  /** Where the last price was: in the zone, under it, over it (null before the first price). */
  side: 'in' | 'below' | 'above' | null
  last: number | null
  /** Placements so far (their order keys end in ".b<n>"). */
  placed: number
  /** When the last placement / pull happened (a pulled order is not re-placed for a short while). */
  placedAt: number | null
  pulledAt: number | null
  /** The ET date a side was last reported on (once a day per side). */
  told: { below: string | null; above: string | null }
  /** A placement was announced; a notice about leaving the zone re-arms it. */
  announced: boolean
  /** When the window ends (epoch ms; null = until stopped, or not known yet). */
  deadline: number | null
  /** The ET date on which there are no more buys (the chase level, or a side set to skipDay). */
  skipDay: string | null
  /** The ET date the pre-market gate last ran, and of the last end-of-day note. */
  gated: string | null
  noted: string | null
  /** A refused placement the user has resumed past (it is tried again). */
  ack?: number
  /** The session's lowest price (a short: highest) seen while watching — the bounce is measured from it. */
  ext?: { date: string; px: number } | null
  /** Why the zone stopped being watched (null = still watching). */
  ended: { reason: BandEndReason; at: number; text: string; last: number | null } | null
}
export type BandEndReason = 'invalid' | 'below' | 'above' | 'expired' | 'filled' | 'exited' | 'stopped' | 'rule'

export interface WorkerRecord {
  id: string
  name: string
  symbol: string
  side: 'long' | 'short'
  status: WorkerStatus
  spec: WorkerSpec
  plan: WorkerPlan
  summary: WorkerSummary
  planHash: string
  checks: RiskCheck[]
  preview: BrokerPreview | null
  runtime: WorkerRuntime | null
  chatId: string | null
  account: string | null
  error: string | null
  createdAt: number
  updatedAt: number
  approvedAt: number | null
  endedAt: number | null
  /** Member of a worker group (shared/groups.ts) under this key. */
  groupId?: string | null
  groupKey?: string | null
  /** A change card (revision draft) for this running worker. */
  revises?: string | null
  /** Plan version of a running worker: 1 at approval, +1 per applied change. */
  version?: number
}

/**
 * One step of a change to a running worker, applied at IBKR (or in our engine) on approval.
 * Prices are what the card showed; the engine re-checks the worker's state before applying.
 */
export type RevisionOp =
  | { kind: 'stop'; lot: number; from: number; to: number }
  | { kind: 'target'; lot: number; from: number; to: number }
  | { kind: 'insurance'; lot: number; from: { trigger: number; stop: number } | null; to: { trigger: number; stop: number } | null }
  | { kind: 'entry'; lot: number; from: number; to: number }
  /** A bought lot: cancel its exits, then a fresh stop (+ target) from the new plan (IBKR cancels a bracket's stop with its target). */
  | { kind: 'rebuildExits'; lot: number; why: string; stop: number; target: number | null }
  /** A lot not bought yet: cancel its bracket, then place the new one. */
  | { kind: 'rebuildLot'; lot: number; why: string }
  /** Nothing bought yet and the lots differ: cancel every order, then place the new plan (placed = orders were at IBKR). */
  | { kind: 'replaceAll'; placed: boolean; why: string }
  | { kind: 'rules'; added: string[]; removed: string[]; changed: string[] }
  | { kind: 'start' }
  | { kind: 'insuranceSetting'; from: InsuranceSetting | null; to: InsuranceSetting }
  /** The buy zone changed: its live orders are pulled and placed again on the new terms. */
  | { kind: 'band'; from: BandPlan | null; to: BandPlan | null }
  | { kind: 'meta'; fields: string[] }

/** The change card of a running worker: what changes, computed by the system. */
export interface RevisionSummary {
  of: string
  ofName: string
  fromVersion: number
  ops: RevisionOp[]
  lines: string[]
  warnings: string[]
}

export interface WorkerOrderRecord {
  id: string
  workerId: string
  planKey: string
  role: 'entry' | 'tp' | 'sl' | 'close'
  lot: number
  tier: number | null
  orderId: number
  permId: number | null
  parentOrderId: number | null
  action: 'BUY' | 'SELL'
  orderType: string
  quantity: number
  limitPrice: number | null
  stopPrice: number | null
  status: string
  filled: number
  avgFillPrice: number | null
  cancelRequested: boolean
  lastError: string | null
  createdAt: number
  updatedAt: number
}

export interface WorkerFillRecord {
  execId: string
  workerId: string
  orderId: number
  role: 'entry' | 'tp' | 'sl' | 'close'
  lot: number
  side: string
  shares: number
  price: number
  time: string
  at: number
}

export interface WorkerEventRecord {
  id: number
  workerId: string
  at: number
  kind: string
  message: string
  data: unknown
}

export interface WorkerDetail extends WorkerRecord {
  orders: WorkerOrderRecord[]
  fills: WorkerFillRecord[]
  events: WorkerEventRecord[]
  /** Live indicator values of a running worker, per rule (id) or the 'when' start (id "start"). */
  marketNow?: { id: string; text: string }[]
}
